import React, { useLayoutEffect } from 'react'
import { STACKED_COLUMN_MAX_ITEMS, centerOut, getCloudLayout } from './utils'

const px = (value: string) => parseFloat(value) || 0

// The rows are worked out from these sizes, so they have to be the exact (fractional) layout sizes
// for the browser to wrap the tags the same way. The bounding rect is exact, but it includes the
// hover and press scale of a tag, in that case the rounded offset size is used.
const layoutSize = (rectSize: number, offsetSize: number) =>
  Math.abs(rectSize - offsetSize) > 1 ? offsetSize : rectSize

const readTags = (cloud: HTMLElement) =>
  Array.from(cloud.children).map(child => {
    const tag = child as HTMLElement
    const rect = tag.getBoundingClientRect()

    return {
      width: layoutSize(rect.width, tag.offsetWidth),
      height: layoutSize(rect.height, tag.offsetHeight),
      weight: Number(tag.dataset.count) || 0,
    }
  })

const fitClouds = (list: HTMLDivElement) => {
  const listStyle = getComputedStyle(list)
  const listHeight = list.clientHeight - px(listStyle.paddingTop) - px(listStyle.paddingBottom)

  // Read every size first and write the layouts afterwards, so the browser lays out only once
  const layouts = Array.from(list.children).map(column => {
    const cloud = column.lastElementChild as HTMLElement | null
    if (!cloud) return null

    const tags = readTags(cloud)
    const indexes = tags.map((_, index) => index)

    // a short column is a single vertical stack: it sizes itself, only the order is set
    if (tags.length <= STACKED_COLUMN_MAX_ITEMS) {
      return { cloud, width: null, order: centerOut(indexes, tags) }
    }

    const columnStyle = getComputedStyle(column)
    const cloudStyle = getComputedStyle(cloud)
    const maxHeight =
      listHeight -
      px(columnStyle.paddingTop) -
      px(columnStyle.paddingBottom) -
      px(cloudStyle.paddingTop) -
      px(cloudStyle.paddingBottom)

    const { width, order } = getCloudLayout(
      tags,
      px(cloudStyle.columnGap),
      px(cloudStyle.rowGap),
      maxHeight,
    )
    const padding =
      cloudStyle.boxSizing === 'border-box'
        ? px(cloudStyle.paddingLeft) + px(cloudStyle.paddingRight)
        : 0

    // half a pixel of slack, far less than a tag, so a row can't break on a rounding error
    return { cloud, width: width + padding + 0.5, order }
  })

  layouts.forEach(layout => {
    if (!layout) return
    if (layout.width !== null) layout.cloud.style.width = `${layout.width}px`
    // the CSS order: the tags stay alphabetical in the DOM, so for the keyboard and screen readers
    layout.order.forEach((tagIndex, position) => {
      ;(layout.cloud.children[tagIndex] as HTMLElement).style.order = String(position)
    })
  })
}

// Sizes the tag cloud of every keyword column to the height that is available for it, and orders
// its tags so the most used keywords are in the centre.
// Set on the DOM nodes directly, so the memoized keyword columns don't re-render on a resize.
export const useCloudLayout = (
  listRef: React.RefObject<HTMLDivElement | null>,
  columns: unknown,
) => {
  // a layout effect, so the columns are sized before the first paint
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return

    let frame = 0
    const fit = () => {
      frame = 0
      if (list.isConnected) fitClouds(list)
    }
    const scheduleFit = () => {
      if (!frame) frame = requestAnimationFrame(fit)
    }

    fit()
    // the tags change size with the viewport (breakpoints) and once the web font is in
    window.addEventListener('resize', scheduleFit)
    document.fonts?.addEventListener('loadingdone', scheduleFit)

    return () => {
      window.removeEventListener('resize', scheduleFit)
      document.fonts?.removeEventListener('loadingdone', scheduleFit)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [listRef, columns])
}
